import { ArrowLeft } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import {
  Form,
  useDataProvider,
  useGetOne,
  useNotify,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Edit } from "@/components/admin/edit";
import { SaveButton } from "@/components/admin/form";
import { FormToolbar } from "@/components/admin/simple-form";
import { MeetingFormBody } from "./MeetingForm";
import type { CrmDataProvider } from "../providers/types";
import type { Meeting } from "./meetingsUtils";

/**
 * MeetingEdit — editor de una junta existente.
 *
 * Pre-carga los attendee_ids desde la view meetings_summary (que ya los
 * devuelve agregados) para que el AutocompleteArrayInput los muestre como
 * seleccionados. Después de guardar, sincroniza el junction table vía RPC.
 */
export function MeetingEdit() {
  const params = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();

  // Pre-fetch view para attendee_ids (la tabla `meetings` no los trae)
  const { data: viewRecord, isPending: isViewLoading } = useGetOne<Meeting>(
    "meetings_summary",
    { id: params.id! },
    { enabled: !!params.id },
  );

  const onSuccess = async (_data: any, variables: any) => {
    const attendeeIds: number[] = variables?.data?.attendee_ids ?? [];
    try {
      await dataProvider.setMeetingAttendees(params.id!, attendeeIds);
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    }
    await queryClient.invalidateQueries({ queryKey: ["meetings"] });
    await queryClient.invalidateQueries({ queryKey: ["meetings_summary"] });
    notify("Junta actualizada", { type: "success" });
    navigate(`/meetings/${params.id}/show`);
  };

  return (
    <div className="px-7 pt-6 pb-7">
      <div className="flex items-center gap-3 mb-5">
        <Link
          to={`/meetings/${params.id}/show`}
          className="musco-btn musco-btn-g"
        >
          <ArrowLeft size={14} /> Detalle
        </Link>
        <h1 className="m-0 text-[28px] font-bold tracking-[-0.025em]">
          Editar junta
        </h1>
      </div>

      <div
        className="musco-hero"
        style={{ maxWidth: 920, marginInline: "auto" }}
      >
        {isViewLoading || !viewRecord ? (
          <p className="text-sm" style={{ color: "var(--musco-muted)" }}>
            Cargando junta…
          </p>
        ) : (
          <Edit
            resource="meetings"
            id={params.id}
            mutationOptions={{ onSuccess }}
            redirect={false}
          >
            <Form
              defaultValues={{
                ...viewRecord,
                // attendee_ids viene del view; el form lo necesita en este nombre
                attendee_ids: (viewRecord.attendees ?? []).map((a) =>
                  Number(a.id),
                ),
              }}
            >
              <MeetingFormBody />
              <FormToolbar>
                <SaveButton label="Guardar cambios" />
              </FormToolbar>
            </Form>
          </Edit>
        )}
      </div>
    </div>
  );
}
